import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSiteContent from "../lib/useSiteContent";
import { useLanguage } from "../hooks/useLanguage";
import { useSectorLabels } from "../lib/useSectorLabels";
import { OuterNav } from "../layouts/PublicNav";
import SeoPageHead from "../components/SeoPageHead";
import { ShareButton } from "../components/ShareButton";
import Logo from "../components/Logo";
import OodelXFooter from "../components/OodelXFooter";
import { Lock, Download } from "@phosphor-icons/react";
import { formatDate } from "../lib/date";

const COLORS = ["#023B24", "#16A34A", "#0055FF", "#F4C430", "#8B5CF6"];

// ITER 67 — Crosstab value → i18n key. Backend returns raw strings like
// "Male", "Punjab", "25-34", "SEC A" — we normalise to `ui_ur_val_*` keys
// so admins can override translations from the Site Content page later.
function crosstabValueKey(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, "_").replace(/[+-]/g, (c) => c === "+" ? "_plus" : "_");
  // Common aliases
  const alias = {
    m: "male", f: "female",
    "18_24": "18_24", "25_34": "25_34", "35_44": "35_44", "45_54": "45_54",
    "55_plus": "55_plus", "55__": "55_plus", "55+": "55_plus",
    kp: "kp", kpk: "kpk",
    ajk: "ajk", gb: "gb", ict: "islamabad",
    // ITER 68 — region label was normalised backend-wide to the full
    // display name "Islamabad Capital Territory" (was "ICT/GB/AJK" / "ICT").
    // Map every variant of that phrase to the same `ui_ur_val_islamabad` key.
    islamabad_capital_territory: "islamabad",
    islamabad_capital: "islamabad",
    sec_a: "sec_a", sec_b: "sec_b", sec_c: "sec_c", sec_d: "sec_d", sec_e: "sec_e",
    a: "sec_a", b: "sec_b", c: "sec_c", d: "sec_d", e: "sec_e",
  };
  const norm = alias[s] || s;
  return `ui_ur_val_${norm}`;
}
function useCrosstabT() {
  const { t, lang } = useLanguage();
  return {
    lang,
    tVal: (raw) => {
      if (lang !== "ur") return raw;
      const k = crosstabValueKey(raw);
      const out = k ? t(k) : null;
      return out && out !== k ? out : raw;   // fallback to raw when no translation
    },
    tCat: (cat) => {
      if (lang !== "ur") return String(cat || "").toUpperCase();
      const k = `ui_ur_cat_${String(cat || "").toLowerCase()}`;
      const out = t(k);
      return out && out !== k ? out : String(cat || "").toUpperCase();
    },
    t,
  };
}

// Per-question visibility badge (matches admin picker colours).
const VIS_META = {
  public:      { label: "Public",      cls: "bg-emerald-600" },
  panelists:   { label: "Panelists",   cls: "bg-sky-600" },
  subscribers: { label: "Subscribers", cls: "bg-violet-600" },
  enterprise:  { label: "Enterprise",  cls: "bg-black" },
};
function TierBadge({ tier }) {
  const m = VIS_META[tier] || VIS_META.public;
  return <span className={`${m.cls} text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider`}>{m.label}</span>;
}

function usePathPrefix(section) {
  const loc = useLocation();
  if (loc.pathname.startsWith(`/panelist/${section}`)) return `/panelist/${section}`;
  return `/${section}`;
}

// -------------------------------------------------------------------------
// SnapshotsList — grouped by WAVE (iter 56).
// Each card = one closed wave; click through to see its questions.
// -------------------------------------------------------------------------
export function SnapshotsList() {
  const [waves, setWaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const prefix = usePathPrefix("snapshots");
  const { lang } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  useEffect(() => {
    setLoading(true);
    api.get(`/public/snapshots/waves?lang=${lang}`)
      .then((r) => setWaves(r.data))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <div className="min-h-screen bg-white themable-public">
      <SeoPageHead
        pageKey="snapshots"
        path="/snapshots"
        defaultTitle="Snapshots — Pakistan One-Shot Surveys | oodel."
        defaultDescription="Every closed wave and its published questions from oodel.'s representative Pakistan consumer panel."
      />
      <OuterNav />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="pb-4 mb-10">
          <div className="overline mb-2 text-mango">SECTION — SNAPSHOTS</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900">Wave archive</h1>
          <p className="text-neutral-500 mt-3 max-w-2xl">Every closed opinion wave. Tap a card to open its questions.</p>
        </div>

        {loading && <div className="text-neutral-500 p-8">Loading…</div>}
        {!loading && waves.length === 0 && (
          <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center text-neutral-500">
            No published snapshots yet.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="snapshots-wave-list">
          {waves.map((w) => (
            <Link
              key={w.wave_id}
              to={`${prefix}/wave/${w.wave_id}`}
              data-testid={`snapshot-wave-${w.wave_id}`}
              className="bg-white border border-neutral-200 rounded-2xl shadow-sm hover:shadow-md hover:border-mango/40 transition-all group flex flex-col overflow-hidden"
            >
              {/* ITER 100 — the wave's first/featured chart (already
                  auto-rendered for WhatsApp/OG sharing) doubles as the
                  listing card's hero image. No admin upload needed.
                  ITER 101 — card size/proportions now match the Trackers
                  listing card exactly (2-col grid, aspect-video, p-8). */}
              {w.image && (
                <div className="aspect-video bg-neutral-50 overflow-hidden" data-testid={`snapshot-wave-hero-${w.wave_id}`}>
                  <img src={w.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                </div>
              )}
              <div className="p-8">
                <div className="overline text-neutral-400 flex items-center gap-2 mb-2">
                  {w.sector && <span className="text-mango">{sectorLabel(w.sector, lang).toUpperCase()}</span>}
                  {w.date && <><span>·</span><span>{formatDate(w.date)}</span></>}
                </div>
                <h2 className="text-2xl font-bold tracking-tight leading-tight text-neutral-900 group-hover:text-mango transition-colors mb-2">
                  {w.name}
                </h2>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-400">
                    {w.visible_question_count} {w.visible_question_count === 1 ? "insight" : "insights"} inside →
                  </span>
                  <div className="flex gap-1">
                    {(w.tiers_present || []).map((t) => <TierBadge key={t} tier={t} />)}
                  </div>
                </div>
                {/* ITER 89/110 — teaser for tier-gated questions in this wave:
                    count + sector tags only, never the specific question
                    wording. Wording adapts to WHY it's locked (signed-in
                    patron with an incomplete profile vs anonymous visitor). */}
                {w.locked_count > 0 && (
                  <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-400 flex items-center gap-1" data-testid={`snapshot-wave-locked-teaser-${w.wave_id}`}>
                    <Lock size={11} />
                    +{w.locked_count} more{w.locked_sectors?.length ? `, covering ${w.locked_sectors.join(", ")}` : ""} — {w.lock_reason === "profile_incomplete" ? "complete profile to unlock" : "unlock"} &rarr;
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
      <OodelXFooter />
    </div>
  );
}

// -------------------------------------------------------------------------
// SnapshotWaveDetail — questions inside a single wave (iter 56).
// -------------------------------------------------------------------------
export function SnapshotWaveDetail() {
  const { wid } = useParams();
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState(null);
  const prefix = usePathPrefix("snapshots");
  const content = useSiteContent();
  const { lang } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  // ITER 61 — Admin CMS toggle. Default TRUE (link visible) unless the
  // admin has explicitly turned it off in Site Content → "Show snapshot
  // detail link". Reads live via the standard site-content poll.
  const showDetailLink = String(content?.show_snapshot_detail_link ?? "true").toLowerCase() !== "false";
  useEffect(() => {
    setPayload(null); setErr(null);
    api.get(`/public/snapshots/waves/${wid}?lang=${lang}`)
      .then((r) => setPayload(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Wave not found"));
  }, [wid, lang]);

  if (err) {
    return (
      <div className="min-h-screen bg-white">
        <OuterNav />
        <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
          <div className="overline mb-2 text-mango">NOT FOUND</div>
          <h1 className="text-3xl font-bold tracking-tight text-neutral-900 mb-3">{err}</h1>
          <Link to={prefix} className="inline-block mt-4 text-mango font-bold">← back to all waves</Link>
        </div>
      </div>
    );
  }
  if (!payload) return <div className="p-12">Loading…</div>;

  return (
    <div className="min-h-screen bg-white themable-public">
      <SeoPageHead
        pageKey="snapshots"
        path={`/snapshots/wave/${wid}`}
        defaultTitle={`${payload.name} — Snapshots | oodel.`}
        defaultDescription={`${payload.snapshots.length} published questions from ${payload.name}.`}
      />
      <OuterNav />
      <div className="max-w-[1200px] mx-auto px-6 py-12" dir={lang === "ur" ? "rtl" : "ltr"}>
        <Link to={prefix} className="overline text-mango hover:underline">← all waves</Link>
        <div className="border-b border-neutral-200 pb-6 mb-10 mt-3">
          <div className="overline mb-2 flex items-center gap-2 flex-wrap">
            {payload.sector && <span className="text-mango">{sectorLabel(payload.sector, lang).toUpperCase()}</span>}
            {payload.closed_at && <><span>·</span><span>Closed {formatDate(payload.closed_at)}</span></>}
            <span className="ml-auto">
              <ShareButton
                url={`${API}/share/snapshots/${wid}`}
                title={payload.name}
                text={`${payload.name} — ${payload.snapshots.length} charts from oodel.`}
                lang={lang}
                testidPrefix="snapshot-wave-share"
              />
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] text-neutral-900" data-testid="snapshot-wave-title">
            {payload.name}
          </h1>
          {payload.wave_disclaimer && (
            <div
              className="mt-4 text-sm text-neutral-600 leading-relaxed max-w-3xl whitespace-pre-line border-l-4 border-mango rounded-r-lg bg-mango/5 pl-4 py-2 italic"
              data-testid="wave-disclaimer"
            >
              {payload.wave_disclaimer}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="snapshot-wave-questions">
          {payload.snapshots.map((s) => (
            <SnapshotQuestionCard key={s.id || s.question_id} snap={s} showDetailLink={showDetailLink} waveDetailPath={`${prefix}/wave/${wid}`} />
          ))}
        </div>
        {payload.snapshots.length === 0 && (
          <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center text-neutral-500">
            None of this wave's questions are visible at your current access level.
          </div>
        )}
      </div>
      <OodelXFooter />
    </div>
  );
}

// Inline question card — bar chart + optional link out to full detail page.
// ITER 90/110 — a PANELIST-gated question (`snap.locked === true`) renders
// in its natural grid position as an opaque "unlock" card instead of the
// real chart — never the real options/overall/question text. `lock_reason`
// picks the right message + CTA (sign up vs complete your profile).
function SnapshotQuestionCard({ snap, showDetailLink, waveDetailPath }) {
  const prefix = usePathPrefix("snapshots");
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  if (snap.locked) {
    const needsProfile = snap.lock_reason === "profile_incomplete";
    const fakeBars = [78, 52, 91, 34];
    return (
      <div className="relative bg-white border border-neutral-200 rounded-2xl shadow-sm p-5 flex flex-col overflow-hidden" data-testid={`snapshot-q-locked-${snap.question_id}`}>
        <div className="space-y-2 flex-1 select-none pointer-events-none blur-sm opacity-40" aria-hidden="true">
          <div className="h-4 w-2/3 bg-neutral-200 rounded mb-3" />
          {fakeBars.map((pct, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
              <div className="col-span-4 h-3 bg-neutral-200 rounded" />
              <div className="col-span-7 h-5 bg-neutral-100 rounded-md relative">
                <div className="h-full rounded-md" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
              </div>
              <div className="col-span-1 h-3 bg-neutral-200 rounded" />
            </div>
          ))}
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/85 backdrop-blur-sm text-center px-5">
          <Lock size={22} className="text-mango" />
          {snap.sector && <div className="overline text-[10px]">{String(snap.sector).toUpperCase()}</div>}
          <div className="font-bold tracking-tight text-neutral-900">{needsProfile ? "Complete your profile to unlock" : "Panelist-only insight"}</div>
          <Link
            to={needsProfile ? "/panelist/profile" : `/register?next=${encodeURIComponent(waveDetailPath)}`}
            className="mt-2 inline-flex items-center gap-1 bg-mango text-white rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-mango/90 transition-colors"
            data-testid={`snapshot-q-locked-cta-${snap.question_id}`}
          >
            {needsProfile ? "Complete profile →" : "Upgrade & unlock →"}
          </Link>
        </div>
      </div>
    );
  }
  const overall = snap.overall || {};
  const opts = snap.options || [];
  const optMap = Object.fromEntries(opts.map((o) => [o.key, o.text || o.key]));
  const rows = Object.entries(overall).map(([k, v]) => ({
    label: optMap[k] || k,
    pct: typeof v === "number" ? (v <= 1 ? +(v * 100).toFixed(1) : +v.toFixed(1)) : 0,
  }));
  const max = Math.max(...rows.map((r) => r.pct), 1);
  // ITER 92 — export the card as a shareable PNG. The visibility tier
  // badge (PUBLIC/PANELISTS/...) and the download button itself are
  // internal admin/UX chrome, not part of the shareable insight — hide
  // both for the one render frame the capture happens on, then restore.
  const handleDownload = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#ffffff", skipFonts: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${(snap.question_text || "snapshot").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60)}.png`;
      a.click();
    } catch (_err) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div ref={cardRef} className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-5 flex flex-col" data-testid={`snapshot-q-${snap.id}`}>
      <div className="flex justify-between items-start gap-3 mb-3">
        <h3 className="font-bold tracking-tight leading-snug text-lg flex-1 text-neutral-900">
          {snap.question_text}
        </h3>
        <div className="flex items-center gap-2 shrink-0">
          {!downloading && <TierBadge tier={snap.visibility || "public"} />}
          {!downloading && (
            <button
              type="button"
              onClick={handleDownload}
              className="w-6 h-6 flex items-center justify-center rounded-full border border-neutral-200 hover:border-mango/50 hover:bg-mango/5 transition-colors"
              title="Download as image"
              data-testid={`snapshot-q-download-${snap.id}`}
            >
              <Download size={13} weight="bold" />
            </button>
          )}
        </div>
      </div>
      {snap.n_sample != null && (
        <div className="text-[11px] text-neutral-400 mb-3">n = {snap.n_sample}</div>
      )}
      <div className="space-y-2 flex-1">
        {rows.map((r, i) => (
          <div key={r.label + i} className="grid grid-cols-12 gap-2 items-center text-sm" title={`${r.label}: ${r.pct}%`}>
            <div className="col-span-4 truncate">{r.label}</div>
            <div className="col-span-7 h-5 bg-neutral-100 rounded-md relative overflow-hidden">
              <div className="h-full rounded-md transition-all group-hover:opacity-80" style={{ width: `${Math.max((r.pct / max) * 100, 1)}%`, background: COLORS[i % COLORS.length] }} />
            </div>
            <div className="col-span-1 text-right font-semibold">{r.pct}%</div>
          </div>
        ))}
      </div>
      {/* ITER 61 — Detail link is admin-gated via `show_snapshot_detail_link`
          CMS toggle. When OFF, hide entirely (no paywall, no ghost link).
          ITER 92 — oodel. wordmark bottom-right, always present (branding
          for the downloaded image, matches the site-wide report footer). */}
      <div className="mt-4 pt-3 border-t border-neutral-100 flex items-end justify-between gap-3">
        {showDetailLink ? (
          <Link
            to={`${prefix}/${snap.id}`}
            className="text-xs font-bold uppercase tracking-wider text-mango hover:underline"
            data-testid={`snap-detail-link-${snap.id}`}
          >
            Open detail + crosstabs →
          </Link>
        ) : <span />}
        <Logo to={false} size="sm" data-testid={`snapshot-q-logo-${snap.id}`} />
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// SnapshotDetail — full single-question view (unchanged — direct links still work).
// -------------------------------------------------------------------------
export function SnapshotDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const { user } = useAuth();
  const prefix = usePathPrefix("snapshots");
  const { lang, t, tVal, tCat } = useCrosstabT();
  // Return the caller-facing text for one option, respecting language.
  const optText = (o) => (lang === "ur" && (o?.text_ur || "").trim()) ? o.text_ur : (o?.text || o?.key || "");
  // ITER 62 — anonymous users NEVER see the crosstab detail page. Even if
  // the underlying snapshot is `visibility=public` (so the API returns 200),
  // detail + crosstabs are a signed-in-only surface. Force the paywall
  // BEFORE calling the data endpoint.
  useEffect(() => {
    if (!user) return;   // will render paywall below
    setS(null); setErr(null);
    api.get(`/snapshots/${id}/data`)
      .then((r) => setS(r.data))
      .catch((e) => setErr({ status: e.response?.status, detail: e.response?.data?.detail || "Not found" }));
  }, [id, user]);

  if (!user) {
    const isUr = lang === "ur";
    return (
      <div className="min-h-screen bg-white themable-public" dir={isUr ? "rtl" : undefined}>
        <OuterNav />
        <div className="max-w-[720px] mx-auto px-6 py-24">
          <div className="border border-neutral-200 rounded-2xl shadow-sm p-8 bg-white" data-testid="snapshot-detail-paywall">
            <div className="flex items-center gap-3 mb-4">
              <Lock size={24} className="text-mango" />
              <div className="overline">{t("ui_ur_snap_pw_pill")}</div>
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-neutral-900 mb-3">
              {t("ui_ur_snap_pw_title")}
            </h1>
            <p className="text-neutral-500 mb-6">
              {t("ui_ur_snap_pw_body")}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Link
                to={`/join?next=${encodeURIComponent(`/panelist/snapshots/${id}`)}`}
                className="bg-mango text-white rounded-full px-5 py-2.5 font-bold hover:bg-mango/90 transition-colors"
                data-testid="snapshot-signup-cta"
              >
                {t("ui_ur_signup_arrow")}
              </Link>
              <Link
                to={`/login?next=${encodeURIComponent(`/panelist/snapshots/${id}`)}`}
                className="border border-neutral-300 rounded-full px-5 py-2.5 font-bold hover:border-mango/50 transition-colors"
                data-testid="snapshot-signin-cta"
              >
                {t("ui_ur_signin")}
              </Link>
              <Link to={prefix} className="border border-neutral-300 rounded-full px-5 py-2.5 font-bold hover:border-mango/50 transition-colors">{t("ui_ur_all_waves")}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (err) {
    // ITER 110 — the backend 403s with the exact message "Complete your
    // profile first..." when a signed-in patron hits panelists-tier
    // content without a finished profile (same wording `take_wave` uses) —
    // pattern-match on it so this generic error page can offer the right
    // next step instead of a dead-end "back to waves" link.
    const needsProfile = /complete your profile/i.test(err.detail || "");
    return (
      <div className="min-h-screen bg-white themable-public">
        <OuterNav />
        <div className="max-w-[720px] mx-auto px-6 py-24">
          <div className="border border-neutral-200 rounded-2xl shadow-sm p-8 bg-white" data-testid="snapshot-detail-error">
            <div className="overline mb-2 text-mango">{needsProfile ? "COMPLETE YOUR PROFILE" : "NOT AVAILABLE"}</div>
            <h1 className="text-3xl font-bold tracking-tight text-neutral-900 mb-3">{needsProfile ? "Complete your profile to view this insight" : "This snapshot isn't available"}</h1>
            <p className="text-neutral-500 mb-6">
              {needsProfile ? "Panelist-only insights need a completed profile so we know your demographic slice is real — takes about 2 minutes." : (err.detail || "The snapshot you are looking for either does not exist or has been unpublished.")}
            </p>
            <div className="flex gap-3">
              {needsProfile && (
                <Link to="/panelist/profile" className="bg-mango text-white rounded-full px-5 py-2.5 font-bold hover:bg-mango/90 transition-colors" data-testid="snapshot-detail-complete-profile-cta">
                  Complete profile →
                </Link>
              )}
              <Link to={prefix} className="border border-neutral-300 rounded-full px-5 py-2.5 font-bold hover:border-mango/50 transition-colors">{t("ui_ur_all_waves")}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!s) return <div className="p-12">Loading…</div>;

  const isUr = lang === "ur";
  return (
    <div className="min-h-screen bg-white themable-public" dir={isUr ? "rtl" : undefined}>
      <OuterNav />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        {s.wave_id && (
          <Link to={`${prefix}/wave/${s.wave_id}`} className="overline text-mango hover:underline">
            {t("ui_ur_back_to_wave")}
          </Link>
        )}
        <div className="border-b border-neutral-200 pb-6 mb-10 mt-3">
          <div className="overline mb-2 text-mango">{t("ui_ur_snapshot_overline")} — {s.date}</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-neutral-900" data-testid="snapshot-title">{isUr && (s.question_text_ur || "").trim() ? s.question_text_ur : s.question_text}</h1>
        </div>

        <div className="border border-neutral-200 rounded-2xl shadow-sm p-6 mb-10 bg-white">
          <div className="flex justify-between items-baseline mb-4">
            <div className="overline text-mango">{t("ui_ur_overall_distribution")}</div>
            {s.n_sample != null && (
              <div className="text-xs text-neutral-400">{t("ui_ur_sample_size")} {s.n_sample}</div>
            )}
          </div>
          <div className="space-y-4">
            {s.options.map((o, i) => {
              const v = s.overall[o.key] ?? 0;
              const label = optText(o);
              return (
                <div key={o.key} className="grid grid-cols-12 gap-3 items-center group" title={`${label}: ${v}%`}>
                  <div className="col-span-3 text-sm font-medium">{label}</div>
                  <div className="col-span-8 h-6 bg-neutral-100 rounded-full relative overflow-hidden">
                    {/* Chart bars stay LTR — data conventionally flows left→right even in RTL locales. */}
                    <div className="h-full rounded-full transition-all group-hover:opacity-80" style={{ width: `${Math.max(v, 0.5)}%`, background: COLORS[i % COLORS.length], direction: "ltr" }} />
                  </div>
                  <div className="col-span-1 text-right font-semibold" style={{ direction: "ltr" }}>{v}%</div>
                </div>
              );
            })}
          </div>
        </div>

        {Object.entries(s.crosstabs || {}).map(([cat, groups]) => (
          <div key={cat} className="mb-10 bg-white border border-neutral-200 rounded-2xl shadow-sm p-6">
            <div className="overline mb-3 text-mango">{t("ui_ur_crosstab")} · {tCat(cat)}</div>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className={`${isUr ? "text-right" : "text-left"} py-3 px-2 uppercase text-xs tracking-widest text-neutral-400`}>{t("ui_ur_group")}</th>
                  {s.options.map((o) => (
                    <th key={o.key} className={`${isUr ? "text-left" : "text-right"} py-3 px-2 uppercase text-xs tracking-widest text-neutral-400`}>{optText(o)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(groups).map(([g, vals]) => (
                  <tr key={g} className="border-b border-neutral-100">
                    <td className="py-3 px-2 font-medium">{tVal(g)}</td>
                    {s.options.map((o) => (
                      <td key={o.key} className={`${isUr ? "text-left" : "text-right"} py-3 px-2`} style={{ direction: "ltr" }}>{vals[o.key] ?? "—"}{vals[o.key] != null ? "%" : ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      <OodelXFooter />
    </div>
  );
}
